import { supabase } from './supabase';
import { BaseService } from './base-service';

/**
 * Interface representing a category
 */
export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Interface representing a subcategory
 */
export interface Subcategory {
  id: string;
  category_id: string;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Interface representing a product category
 */
export interface ProductCategory {
  id: string;
  subcategory_id: string;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
  product_count: number;
  specifications_schema?: any;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Service for managing categories, subcategories, and product categories
 */
export class CategoryService extends BaseService {
  constructor() {
    super(supabase);
  }

  // Category methods
  
  /**
   * Get all categories
   * @returns Array of categories or empty array if none found
   */
  async getCategories(): Promise<Category[]> {
    try {
      const { data, error } = await this.supabase
        .from('categories')
        .select('*')
        .order('display_order', { ascending: true });
        
      if (error) throw error;
      return data || [];
    } catch (error) {
      this.handleError(error, 'fetching categories');
      return [];
    }
  }
  
  /**
   * Get a category by ID
   * @param id The category ID
   * @returns The category or null if not found
   */
  async getCategory(id: string): Promise<Category | null> {
    try {
      const { data, error } = await this.supabase
        .from('categories')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'fetching category');
      return null;
    }
  }
  
  /**
   * Get a category by slug
   * @param slug The category slug
   * @returns The category or null if not found
   */
  async getCategoryBySlug(slug: string): Promise<Category | null> {
    try {
      // First check if the category exists and how many match this slug
      const { data: categories, error: checkError } = await this.supabase
        .from('categories')
        .select('*')
        .eq('slug', slug);
      
      if (checkError) throw checkError;
      
      // If no categories found, return null
      if (!categories || categories.length === 0) {
        return null;
      }
      
      // If multiple categories found, use the first one but log a warning
      if (categories.length > 1) {
        console.warn(`Multiple categories found with slug '${slug}'. Using the first one.`);
      }
      
      // Return the first category
      return categories[0];
    } catch (error) {
      this.handleError(error, 'fetching category by slug');
      return null;
    }
  }
  
  /**
   * Create a new category
   * @param category The category data
   * @returns The created category or null if creation failed
   */
  async createCategory(category: Omit<Category, 'id' | 'created_at' | 'updated_at'>): Promise<Category | null> {
    try {
      const categoryWithTimestamps = this.addTimestamps(category, true);
      
      const { data, error } = await this.supabase
        .from('categories')
        .insert(categoryWithTimestamps)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'creating category');
      return null;
    }
  }
  
  /**
   * Update an existing category
   * @param id The category ID
   * @param updates The category updates
   * @returns The updated category or null if update failed
   */
  async updateCategory(id: string, updates: Partial<Category>): Promise<Category | null> {
    try {
      const updatesWithTimestamp = this.addTimestamps(updates);
      
      const { data, error } = await this.supabase
        .from('categories')
        .update(updatesWithTimestamp)
        .eq('id', id)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'updating category');
      return null;
    }
  }
  
  /**
   * Delete a category
   * @param id The category ID
   * @returns True if deletion was successful, false otherwise
   */
  async deleteCategory(id: string): Promise<boolean> {
    try {
      // First check if there are any subcategories associated with this category
      const { data: subcategories, error: subcatError } = await this.supabase
        .from('subcategories')
        .select('id')
        .eq('category_id', id);
        
      if (subcatError) throw subcatError;
      
      if (subcategories && subcategories.length > 0) {
        throw new Error('Cannot delete category with existing subcategories. Delete subcategories first.');
      }
      
      // If no subcategories, proceed with deletion
      const { error } = await this.supabase
        .from('categories')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return true;
    } catch (error) {
      this.handleError(error, 'deleting category');
      return false;
    }
  }

  // Subcategory methods
  
  /**
   * Get subcategories by category ID
   * @param categoryId The category ID
   * @returns Array of subcategories or empty array if none found
   */
  async getSubcategoriesByCategoryId(categoryId: string): Promise<Subcategory[]> {
    try {
      const { data, error } = await this.supabase
        .from('subcategories')
        .select('*')
        .eq('category_id', categoryId)
        .order('display_order', { ascending: true });
        
      if (error) throw error;
      return data || [];
    } catch (error) {
      this.handleError(error, 'fetching subcategories by category ID');
      return [];
    }
  }
  
  /**
   * Get subcategories by category ID (alias for getSubcategoriesByCategoryId)
   * @param categoryId The category ID
   * @returns Array of subcategories or empty array if none found
   */
  async getSubcategories(categoryId: string): Promise<Subcategory[]> {
    return this.getSubcategoriesByCategoryId(categoryId);
  }
  
  /**
   * Create a new subcategory
   * @param subcategory The subcategory data
   * @returns The created subcategory or null if creation failed
   */
  async createSubcategory(subcategory: Omit<Subcategory, 'id' | 'created_at' | 'updated_at'>): Promise<Subcategory | null> {
    try {
      const subcategoryWithTimestamps = this.addTimestamps(subcategory, true);
      
      const { data, error } = await this.supabase
        .from('subcategories')
        .insert(subcategoryWithTimestamps)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'creating subcategory');
      return null;
    }
  }
  
  /**
   * Get a subcategory by ID
   * @param id The subcategory ID
   * @returns The subcategory or null if not found
   */
  async getSubcategory(id: string): Promise<Subcategory | null> {
    try {
      const { data, error } = await this.supabase
        .from('subcategories')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'fetching subcategory');
      return null;
    }
  }
  
  /**
   * Get a subcategory by slug and category slug
   * @param categorySlug The category slug
   * @param subcategorySlug The subcategory slug
   * @returns The subcategory or null if not found
   */
  async getSubcategoryBySlug(categorySlug: string, subcategorySlug: string): Promise<Subcategory | null> {
    try {
      // First get the category ID from the slug
      const category = await this.getCategoryBySlug(categorySlug);
      if (!category) return null;
      
      // Then get the subcategory using the category ID and subcategory slug
      const { data, error } = await this.supabase
        .from('subcategories')
        .select('*')
        .eq('category_id', category.id)
        .eq('slug', subcategorySlug)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'fetching subcategory by slug');
      return null;
    }
  }
  
  /**
   * Update an existing subcategory
   * @param id The subcategory ID
   * @param updates The subcategory updates
   * @returns The updated subcategory or null if update failed
   */
  async updateSubcategory(id: string, updates: Partial<Subcategory>): Promise<Subcategory | null> {
    try {
      const updatesWithTimestamp = this.addTimestamps(updates);
      
      const { data, error } = await this.supabase
        .from('subcategories')
        .update(updatesWithTimestamp)
        .eq('id', id)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'updating subcategory');
      return null;
    }
  }
  
  /**
   * Delete a subcategory
   * @param id The subcategory ID
   * @returns True if deletion was successful, false otherwise
   */
  async deleteSubcategory(id: string): Promise<boolean> {
    try {
      // First check if there are any product categories associated with this subcategory
      const { data: productCategories, error: prodCatError } = await this.supabase
        .from('product_categories')
        .select('id')
        .eq('subcategory_id', id);
        
      if (prodCatError) throw prodCatError;
      
      if (productCategories && productCategories.length > 0) {
        throw new Error('Cannot delete subcategory with existing product categories. Delete product categories first.');
      }
      
      // If no product categories, proceed with deletion
      const { error } = await this.supabase
        .from('subcategories')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return true;
    } catch (error) {
      this.handleError(error, 'deleting subcategory');
      return false;
    }
  }

  // Product category methods
  
  /**
   * Get product categories by subcategory ID
   * @param subcategoryId The subcategory ID
   * @returns Array of product categories or empty array if none found
   */
  async getProductCategoriesBySubcategoryId(subcategoryId: string): Promise<ProductCategory[]> {
    try {
      const { data, error } = await this.supabase
        .from('product_categories')
        .select('*')
        .eq('subcategory_id', subcategoryId)
        .order('display_order', { ascending: true });
        
      if (error) throw error;
      return data || [];
    } catch (error) {
      this.handleError(error, 'fetching product categories');
      return [];
    }
  }
  
  /**
   * Create a new product category
   * @param productCategory The product category data
   * @returns The created product category or null if creation failed
   */
  async createProductCategory(productCategory: Omit<ProductCategory, 'id' | 'created_at' | 'updated_at'>): Promise<ProductCategory | null> {
    try {
      // Ensure product_count starts at 0 for new categories
      const productCategoryWithDefaults = {
        ...productCategory,
        product_count: 0
      };
      
      const productCategoryWithTimestamps = this.addTimestamps(productCategoryWithDefaults, true);
      
      const { data, error } = await this.supabase
        .from('product_categories')
        .insert(productCategoryWithTimestamps)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'creating product category');
      return null;
    }
  }
  
  /**
   * Get a product category by ID
   * @param id The product category ID
   * @returns The product category or null if not found
   */
  async getProductCategory(id: string): Promise<ProductCategory | null> {
    try {
      const { data, error } = await this.supabase
        .from('product_categories')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'fetching product category');
      return null;
    }
  }
  
  /**
   * Get a product category by slug and subcategory slug and category slug
   * @param categorySlug The category slug
   * @param subcategorySlug The subcategory slug
   * @param productCategorySlug The product category slug
   * @returns The product category or null if not found
   */
  async getProductCategoryBySlug(
    categorySlug: string,
    subcategorySlug: string,
    productCategorySlug: string
  ): Promise<ProductCategory | null> {
    try {
      // First get the subcategory using the category and subcategory slugs
      const subcategory = await this.getSubcategoryBySlug(categorySlug, subcategorySlug);
      if (!subcategory) return null;
      
      // Then get the product category using the subcategory ID and product category slug
      const { data, error } = await this.supabase
        .from('product_categories')
        .select('*')
        .eq('subcategory_id', subcategory.id)
        .eq('slug', productCategorySlug)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'fetching product category by slug');
      return null;
    }
  }
  
  /**
   * Update an existing product category
   * @param id The product category ID
   * @param updates The product category updates
   * @returns The updated product category or null if update failed
   */
  async updateProductCategory(id: string, updates: Partial<ProductCategory>): Promise<ProductCategory | null> {
    try {
      const updatesWithTimestamp = this.addTimestamps(updates);
      
      const { data, error } = await this.supabase
        .from('product_categories')
        .update(updatesWithTimestamp)
        .eq('id', id)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'updating product category');
      return null;
    }
  }
  
  /**
   * Delete a product category
   * @param id The product category ID
   * @returns True if deletion was successful, false otherwise
   */
  async deleteProductCategory(id: string): Promise<boolean> {
    try {
      // First check if there are any products associated with this product category
      const { data: products, error: productsError } = await this.supabase
        .from('products')
        .select('id')
        .eq('product_category_id', id);
        
      if (productsError) throw productsError;
      
      if (products && products.length > 0) {
        throw new Error('Cannot delete product category with existing products. Delete or reassign products first.');
      }
      
      // If no products, proceed with deletion
      const { error } = await this.supabase
        .from('product_categories')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return true;
    } catch (error) {
      this.handleError(error, 'deleting product category');
      return false;
    }
  }
  
  /**
   * Update product count for a product category
   * @param id The product category ID
   * @param increment The amount to increment (positive) or decrement (negative)
   * @returns The updated product category or null if update failed
   */
  async updateProductCount(id: string, increment: number): Promise<ProductCategory | null> {
    try {
      // First get the current product count
      const { data: currentCategory, error: fetchError } = await this.supabase
        .from('product_categories')
        .select('product_count')
        .eq('id', id)
        .single();
        
      if (fetchError) throw fetchError;
      
      // Calculate new count (ensure it doesn't go below 0)
      const newCount = Math.max(0, (currentCategory.product_count || 0) + increment);
      
      // Update with timestamp
      const updates = this.addTimestamps({ product_count: newCount });
      
      const { data, error } = await this.supabase
        .from('product_categories')
        .update(updates)
        .eq('id', id)
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'updating product count');
      return null;
    }
  }
}
